/* eslint-disable complexity */
import { FC, MouseEvent, SyntheticEvent, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAutoFocusOnMount, useCustomRegister } from '../../../hooks';
import { CreateEventFormData } from '../../../models/dto';
import { createEventFlowStyles } from './create-event-flow.styles';
import { Box, IconButton, MenuItem, Typography } from '@mui/material';
import { Select, TextField, TextMarkdownEditor } from '../../inputs';
import { flexColumn, flexRow } from '../../../styles';
import {
	Colors,
	DaysOfWeekAbbr,
	EventTypeEnum,
	getDefaultMonthlyRepeatValues,
	ScheduleRepeatOptions,
} from '../../../constants';
import { CoreLabeledCheckbox, Option } from '../../_core';
import { EditIcon, FlashIcon, PromocodeIcon, SubHeaderIcon, TextIcon, Ticket2Icon } from '../../../icons';
import {
	defaultEventDuration,
	EventPeriodType,
	getEventPriceRangeString,
	getTimeZones,
	priceFormatter,
	ScheduleRepeatTypesEnum,
	TimeZonesEnum,
} from '@sweatpals/shared';
import { map } from 'lodash-es';
import { Showable } from '../../utils';
import { PriceTierChip } from '../prices-modal/price-tier-chip.component';
import { formatInTimeZone } from 'date-fns-tz';
import { PromocodeChip } from '../promocodes-modal/promocode-chip.component';
import { borderlessInputStyles } from '../../community-flow/create-community/create-community-flow.styles';
import { ActivityAutocomplete } from '../../activity-autocomplete/activity-autocomplete.component';
import { clsx, GetPlacesCallback } from '../../../utils';
import { PlaceAutocompleteWithOnline } from './place-autocomplete-with-online.component';
import { EventDateTimePicker } from '../event-date-time-picker';

const autoFocusClass = 'js-autofocus';

export interface MainInfoProps {
	showRepeatScheduleModal: boolean;
	getPlaceOptions: GetPlacesCallback;
	activityOptions: Option[];
	periodType?: EventPeriodType;
	isDateFieldsDisabled?: boolean;
	showSubHeaderInput?: boolean | undefined;
	editOnlyThisEventUrl?: string;
	onPlaceInputFocus?(e: SyntheticEvent): void;
	setIsPricesModalOpen(value: boolean | ((s: boolean) => boolean)): void;
	setIsPromocodesModalOpen(value: boolean | ((s: boolean) => boolean)): void;
	setIsConfirmClearRepeatModalOpen(value: boolean | ((s: boolean) => boolean)): void;
	setIsRepeatScheduleModalOpen(value: boolean | ((s: boolean) => boolean)): void;
	onAddDescriptionButtonClick?(e: MouseEvent<HTMLElement>): void;
	onAddSubheaderButtonClick?(e: MouseEvent<HTMLElement>): void;
}

export const MainInfo: FC<MainInfoProps> = props => {
	const {
		showRepeatScheduleModal,
		showSubHeaderInput,
		activityOptions,
		isDateFieldsDisabled,
		periodType,
		editOnlyThisEventUrl,
		getPlaceOptions,
		onPlaceInputFocus,
		setIsPricesModalOpen,
		setIsPromocodesModalOpen,
		setIsConfirmClearRepeatModalOpen,
		setIsRepeatScheduleModalOpen,
		onAddDescriptionButtonClick,
		onAddSubheaderButtonClick,
	} = props;

	const form = useFormContext<CreateEventFormData>();

	useAutoFocusOnMount({
		autoFocusClass,
	});

	const data = form.watch();
	const errors = form.formState.errors;

	const startDate = data?.startDate;
	const endDate = data?.endDate;
	const scheduleRepeat = data?.scheduleRepeat;

	const { register } = useCustomRegister(form);

	const onChangeStartDate = useCallback(
		(value: Date | string | number | null): void => {
			if (!value) {
				form.setValue('startDate', null);

				return;
			}

			const diff = startDate && endDate ? endDate.getTime() - startDate.getTime() : defaultEventDuration;

			const newStartDate = new Date(value);
			const newEndDate = new Date(newStartDate);
			newEndDate.setTime(newEndDate.getTime() + diff);

			if (scheduleRepeat?.typeId === ScheduleRepeatTypesEnum.Monthly) {
				form.setValue('scheduleRepeat', { ...scheduleRepeat, ...getDefaultMonthlyRepeatValues(newStartDate) });
			}

			form.setValue('endDate', newEndDate);
			form.setValue('startDate', newStartDate);
		},
		[endDate, scheduleRepeat, form, startDate]
	);

	const onChangeEndDate = useCallback(
		(value: Date | string | number | null): void => {
			form.setValue('endDate', value ? new Date(value) : null);
		},
		[form]
	);

	const timeZones = useMemo(() => getTimeZones(data.startDate), [data.startDate]);

	const repeatType = ScheduleRepeatOptions.find(option => option.id === scheduleRepeat?.typeId)?.label;
	const weeklyDays =
		scheduleRepeat?.typeId === ScheduleRepeatTypesEnum.Weekly
			? ` on ${scheduleRepeat?.dayOfWeekIds?.map(dayId => DaysOfWeekAbbr[dayId]).join('/')}`
			: '';
	const intervalText =
		scheduleRepeat?.interval && scheduleRepeat?.interval > 1 ? ` every ${scheduleRepeat?.interval} weeks` : '';
	const endDateText =
		scheduleRepeat?.endDate instanceof Date
			? `until ${formatInTimeZone(scheduleRepeat.endDate, data?.tzid ?? TimeZonesEnum.Central, 'EEE MMM dd')}`
			: 'indefinitely';
	const repeatScheduleShortDescription = scheduleRepeat
		? `Event repeats: ${intervalText || repeatType?.toLowerCase()}${weeklyDays} ${endDateText}`
		: undefined;

	const eventFlowStyles = createEventFlowStyles();

	return (
		<Box
			sx={flexColumn({
				gap: { xs: '24px', md: '32px' },
			})}
		>
			<TextField
				{...register('name')}
				onChange={e => {
					form.setValue('name', e.target.value?.replace(/\n/g, ''));
				}}
				sx={borderlessInputStyles}
				placeholder=' Enter Event Name'
				autoFocus
				multiline
				minRows={1}
				maxRows={2}
				inputProps={{
					className: autoFocusClass,
					wrap: 'soft',
				}}
			/>

			<Box sx={flexColumn()}>
				<Box sx={eventFlowStyles.mainInfoBox}>
					<Box
						sx={isDateFieldsDisabled ? {
							p: '4px 4px 10px',
							borderRadius: '8px',
							border: `1px solid ${Colors['F19239']}`,
							display: 'flex',
							flexDirection: 'column',
							rowGap: '10px',
						} : undefined}
					>
						<Box
							sx={isDateFieldsDisabled ? {
								p: '12px',
								borderRadius: '8px',
								backgroundColor: Colors['F19239_a10'],
								border: `1px solid ${Colors['F19239_a10']}`,
							} : undefined}
						>
							<Box
								sx={flexColumn({
									flexDirection: { xs: 'column', md: 'row' },
									gap: '8px',
									flex: 1,
								})}
							>
								<EventDateTimePicker
									{...register('startDate', { type: 'datetime-local' })}
									onChange={onChangeStartDate}
									timezone={data.tzid}
									disablePast
									fullWidth
									disabled={isDateFieldsDisabled}
								/>

								<EventDateTimePicker
									{...register('endDate', { type: 'datetime-local' })}
									minDate={data.startDate ?? undefined}
									onChange={onChangeEndDate}
									timezone={data.tzid}
									disablePast
									fullWidth
									disabled={isDateFieldsDisabled}
								/>

								<Select
									{...register('tzid')}
									IconComponent={() => null}
									controlSx={{
										flexShrink: 0,
									}}
									disabled={isDateFieldsDisabled}
									sx={eventFlowStyles.timezoneSelect}
									renderValue={id => (
										<Typography variant='inherit'>{timeZones[id as TimeZonesEnum]?.abbr ?? (id as string)}</Typography>
									)}
								>
									{map(timeZones, ({ abbr, offset }, id) => (
										<MenuItem
											key={id}
											value={id}
											sx={{
												gap: '10px',
											}}
										>
											<Typography
												sx={{
													color: Colors.white,
												}}
											>
												{abbr} ({offset})
											</Typography>
										</MenuItem>
									))}
								</Select>
							</Box>
							<Showable hide={!data?.startDate || periodType === EventPeriodType.OnlyThis}>
								<Box sx={flexRow(eventFlowStyles.repeatScheduleCheckboxRow)}>
									<CoreLabeledCheckbox
										disabled={isDateFieldsDisabled}
										label={repeatScheduleShortDescription ?? 'Set this event to repeat'}
										error={!!errors?.scheduleRepeat}
										checked={
											(scheduleRepeat && scheduleRepeat.typeId !== ScheduleRepeatTypesEnum.Never) ||
											showRepeatScheduleModal
										}
										onChange={() => {
											if (scheduleRepeat && scheduleRepeat.typeId !== ScheduleRepeatTypesEnum.Never) {
												setIsConfirmClearRepeatModalOpen(true);
											} else {
												setIsRepeatScheduleModalOpen(true);
											}
										}}
										labelSx={eventFlowStyles.repeatScheduleCheckboxLabel}
										sx={eventFlowStyles.repeatScheduleCheckbox}
										size='small'
									/>

									<Showable show={scheduleRepeat && scheduleRepeat.typeId !== ScheduleRepeatTypesEnum.Never}>
										<IconButton
											onClick={() => {
												setIsRepeatScheduleModalOpen(true);
											}}
											disabled={isDateFieldsDisabled}
											sx={eventFlowStyles.repeatScheduleEditButton}
										>
											<EditIcon />
										</IconButton>
									</Showable>
								</Box>
							</Showable>
						</Box>
						<Showable show={isDateFieldsDisabled}>
							<Typography sx={eventFlowStyles.repeatScheduleWarningText}>
								⚠️ Date editing is disabled to avoid losing RSVPs. Switch to ‘
								{editOnlyThisEventUrl ? (
									<Box component="a" href={editOnlyThisEventUrl} target="_self" rel="noreferrer">
										Only this
									</Box>
								) : (
									<Box component="span">Only this</Box>
								)}
								’ mode to edit the date of a single event.
							</Typography>
						</Showable>
					</Box>

					<Box
						sx={flexRow({
							alignItems: 'center',
							flex: 1,
						})}
					>
						<PlaceAutocompleteWithOnline
							{...register('addressValue', {
								errorFields: ['address', 'addressValue', 'onlineUrl', 'isOnlineEvent'],
								passOnBlur: false,
								passRef: false,
							})}
							freeSolo
							values={data?.address ? [data.address] : []}
							hideOptions={data?.isOnlineEvent}
							onPlaceInputFocus={onPlaceInputFocus}
							getPlaceOptions={getPlaceOptions}
							placeholder={data?.address ? '' : 'Add event location or virtual link'}
							sx={eventFlowStyles.addressSelect(!!data?.address)}
							fullWidth
							address={data?.address}
							isOnlineEvent={!!data?.isOnlineEvent}
							onlineUrl={data.onlineUrl}
							setAddress={value => form.setValue('address', value)}
							setIsOnlineEvent={value => form.setValue('isOnlineEvent', value)}
							setOnlineUrl={value => form.setValue('onlineUrl', value)}
						></PlaceAutocompleteWithOnline>
					</Box>

					<Showable show={showSubHeaderInput && onAddSubheaderButtonClick}>
						<Box
							onClick={e => {
								e.stopPropagation();
								onAddSubheaderButtonClick?.(e);
							}}
							sx={{ cursor: 'pointer' }}
						>
							<TextMarkdownEditor
								markdown={data?.subHeaderMarkdown && data?.subHeaderMarkdown.length ? data?.subHeaderMarkdown : ''}
								readOnly={true}
								startAdornment={<SubHeaderIcon />}
								placeholder='Add event subheader'
								maxRows={5}
								minRows={1}
							/>
						</Box>
					</Showable>

					<Showable show={showSubHeaderInput && !onAddSubheaderButtonClick}>
						<TextField
							{...register('subHeaderMarkdown')}
							placeholder='Add event subheader'
							multiline
							minRows={1}
							maxRows={5}
							startAdornment={<SubHeaderIcon />}
							fullWidth
						/>
					</Showable>

					<Showable show={onAddDescriptionButtonClick}>
						<Box
							onClick={e => {
								e.stopPropagation();
								onAddDescriptionButtonClick?.(e);
							}}
							sx={{ cursor: 'pointer' }}
						>
							<TextMarkdownEditor
								markdown={data?.description && data?.description.length ? data?.description : ''}
								readOnly={true}
								startAdornment={<TextIcon />}
								placeholder='Add event description'
								maxRows={5}
								minRows={1}
							/>
						</Box>
					</Showable>

					<Showable show={!onAddDescriptionButtonClick}>
						<TextField
							{...register('description')}
							placeholder='Add event description'
							multiline
							minRows={1}
							maxRows={10}
							startAdornment={<TextIcon />}
							fullWidth
						/>
					</Showable>

					<Box
						sx={eventFlowStyles.ticketsDisplay}
						onClick={() => {
							setIsPricesModalOpen(true);
						}}
					>
						<Box sx={eventFlowStyles.ticketsDisplayBox}>
							<Box
								component={Ticket2Icon}
								sx={{
									flexShrink: 0,
									width: '20px',
								}}
							/>

							{data?.tiers?.length ? (
								<Box sx={eventFlowStyles.ticketsRow}>
									{data?.tiers?.map(tier => (
										<PriceTierChip key={tier.key} data={tier} />
									))}
								</Box>
							) : (
								<Typography sx={eventFlowStyles.ticketsPlaceholder}>Tickets</Typography>
							)}
						</Box>

						<Box sx={eventFlowStyles.ticketsSmallInfoBox}>
							<Typography
								sx={clsx(eventFlowStyles.ticketsSummaryText, {
									color: [!!errors.tiers, !!errors.minimalDonationAmount, !!errors.type].filter(Boolean).length
										? Colors.error
										: Colors.white,
								})}
							>
								{data?.type === EventTypeEnum.Paid
									? data?.tiers?.length
										? getEventPriceRangeString(data.tiers)
										: 'Paid'
									: data?.type === EventTypeEnum.Donation
									? data?.minimalDonationAmount && data?.minimalDonationAmount > 0
										? `Min Donation ${priceFormatter(data.minimalDonationAmount)}`
										: 'Donation'
									: 'Free'}
							</Typography>

							<Box
								component={EditIcon}
								sx={{
									width: '24px',
									minWidth: '20px',
									cursor: 'pointer',
									color: [!!errors.tiers, !!errors.minimalDonationAmount, !!errors.type].filter(Boolean).length
										? Colors.error
										: Colors.white,
								}}
								onClick={() => {
									setIsPricesModalOpen(true);
								}}
							/>
						</Box>
					</Box>

					{data?.discounts?.length ? (
						<Box
							sx={eventFlowStyles.ticketsDisplay}
							onClick={() => {
								setIsPricesModalOpen(true);
								setIsPromocodesModalOpen(true);
							}}
						>
							<Box sx={eventFlowStyles.ticketsDisplayBox}>
								<Box
									component={PromocodeIcon}
									sx={{
										width: '20px',
										height: '20px',
									}}
								/>

								<Box sx={eventFlowStyles.ticketsRow}>
									{data?.discounts?.map(discount => (
										<PromocodeChip key={discount.code} data={discount} />
									))}
								</Box>
							</Box>

							<Box
								component={EditIcon}
								sx={{
									minWidth: '20px',
									width: '24px',
									cursor: 'pointer',
									color: [!!errors.discounts].filter(Boolean).length ? Colors.error : Colors.white,
								}}
								onClick={() => {
									setIsPricesModalOpen(true);
									setIsPromocodesModalOpen(true);
								}}
							/>
						</Box>
					) : null}

					<ActivityAutocomplete
						{...register('fitnessTypes', {
							fallbackValue: [],
							passOnBlur: false,
						})}
						onChange={values => {
							form.setValue('fitnessTypes', values, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						sx={eventFlowStyles.activitySelect}
						placeholder='Add activity'
						startAdornment={<Box component={FlashIcon} />}
						options={activityOptions}
						allowSelectMasteryLevel
					/>
				</Box>
			</Box>
		</Box>
	);
};
